import React from "react";
import {
  BottomSheet,
  Button,
  Modal,
  useIsMobile,
} from "../../../components/ui";
import { ageOn, formatEnumLabel, formatTimeRange } from "../clientMapping";
// The three habit answers are constants with wording of their own:
// `OCCASIONAL` reads as "Occasional travel", not as "Occasional".
import { habitLabel } from "../../preconsultation/preconsultationSteps";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import type { PreconsultationFormAnswers } from "../../preconsultation/services/preconsultationFormsService";

/** What an unanswered question shows, the same dash the Details tab uses. */
const DASH = "-";

const orDash = (value: string): string => value.trim() || DASH;

/** A list on one line, and the dash where nothing was named. */
const asLine = (values: string[]): string => values.join(", ") || DASH;

/**
 * How old they are, out of whichever of the two answers the form carries.
 *
 * The form asks for a date of birth and takes an age beside it, and either can
 * be the only one filled in — so reading `age` alone showed a dash to a
 * practitioner whose client had just told them the day they were born. The
 * age as given wins where there is one: it is what the client said, and
 * counting back from a date the record may have got from somewhere else is a
 * second opinion nobody asked for.
 */
const ageOf = (answers: PreconsultationFormAnswers): string => {
  const years = answers.age ?? ageOn(answers.dateOfBirth);
  return years === null ? DASH : `${years}`;
};

/** `180 cm`, and the dash where either half is missing. */
const measure = (value: string, unit: string): string =>
  value.trim() && unit.trim() ? `${value.trim()} ${unit.trim()}` : DASH;

/**
 * A pair of times, or the answer that there is no fixed pair.
 *
 * The flag matters: "no fixed hours" is an answer a practitioner can plan
 * around and a blank is not, and both arrive as two empty times.
 */
const hours = (from: string, to: string, none: boolean): string =>
  none ? "No fixed hours" : formatTimeRange(from, to);

/** One question and its answer. */
const Answer: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <p className="text-[12px] 3xl:text-[13px] text-ink-muted">{label}</p>
    <p className="mt-0.5 text-[14px] 3xl:text-[15px] text-ink break-words">
      {value}
    </p>
  </div>
);

/**
 * A card's worth of answers.
 *
 * `columns` is how many the card holds on a wide screen; every one collapses to
 * a single column on a narrow one, because a label and its answer stacked is
 * still readable and two half-width columns are not.
 */
const Section: React.FC<{
  title: string;
  columns: 2 | 3 | 4;
  children: React.ReactNode;
}> = ({ title, columns, children }) => (
  <section className="rounded-lg border border-line bg-[#FCFCFD] px-4 py-4">
    <h3 className="text-[14px] 3xl:text-[15px] font-medium text-ink">
      {title}
    </h3>
    <div
      className={`mt-3 grid gap-x-4 gap-y-3 grid-cols-1 ${
        columns === 2
          ? "sm:grid-cols-2"
          : columns === 3
          ? "sm:grid-cols-2 lg:grid-cols-3"
          : "sm:grid-cols-2 lg:grid-cols-4"
      }`}
    >
      {children}
    </div>
  </section>
);

/** The pill beside the name: what is being asked of the reader, in a word. */
const PendingPill: React.FC = () => (
  <span className="flex-shrink-0 rounded-full border border-[#F0B429] px-2.5 py-0.5 text-[12px] text-[#B7791F]">
    Approval pending
  </span>
);

export interface PreconsultationReviewModalProps {
  /** How the dialog heads itself — "Mr. Subham Patil". */
  clientName: string;
  answers: PreconsultationFormAnswers;
  /**
   * Whether this workspace may still rule on a submission, read once by the
   * screen above — see `ClientDetailPage`.
   *
   * It lives here rather than on the strip that opens this, because this is
   * where the deciding happens now. Reading the answers is not a write and was
   * never gated; taking them onto a client's record is.
   */
  gate: SubscriptionGate;
  onAccept: () => void;
  onReject: () => void;
  onClose: () => void;
}

/**
 * The submitted pre-consultation, read whole — and ruled on.
 *
 * Every answer as it came back, grouped the way the form asked them, so the
 * decision underneath it is made against the whole thing rather than against
 * the two fields that happen to fit on a strip. Nothing here is editable: the
 * practitioner is ruling on what their client said, and a screen that let them
 * quietly correct it first would be recording an answer nobody gave. What is
 * wrong goes back to the client with a reason — that is what Reject is.
 *
 * This is the only place either decision is made. The strip that opens it used
 * to carry Accept and Reject as well, which put the question to somebody who
 * had not read anything yet: the strip holds a name and a timestamp, and the
 * submission being judged is in here. Now the strip opens the answers and this
 * rules on them.
 *
 * Two shells. On a wide screen a centred panel of a fixed, deliberately short
 * height: the answers scroll inside it while the name and the two buttons stay
 * put, so the decision is never a screenful below the part being read.
 *
 * On a phone it is a sheet instead — not the same panel with different
 * corners. A dialog 960px wide and 620 tall has nowhere to go on a handset,
 * and what a phone does with a long record it has to act on is bring it up
 * from the bottom edge with the actions under the thumb. The sheet lays the
 * pair out as equal halves of its footer, which is what every pair of actions
 * on a phone in this app does — see `BottomSheet`.
 */
const PreconsultationReviewModal: React.FC<PreconsultationReviewModalProps> = ({
  clientName,
  answers,
  gate,
  onAccept,
  onReject,
  onClose,
}) => {
  // Which shell the answers are put in. Not a `md:` class, for the reason
  // above — the same call `PlanFormModal` makes.
  const isMobile = useIsMobile();

  /**
   * The two decisions, written once for both shells.
   *
   * Accept is filled and Reject is not, because accepting is the ordinary
   * outcome — the pairing `Button`'s `approve` variant exists for. In the
   * sheet they come out equal halves of the footer row; in the dialog they
   * keep their own widths against the right edge.
   */
  const decisions = (
    <>
      <Button
        size="md"
        variant="dangerQuiet"
        onClick={onReject}
        {...gate.props("review a pre-consultation form")}
      >
        Reject
      </Button>
      <Button
        size="md"
        variant="approve"
        onClick={onAccept}
        {...gate.props("review a pre-consultation form")}
      >
        Accept
      </Button>
    </>
  );

  const sections = (
    <div className="flex flex-col gap-3">
      <Section title="Personal details" columns={4}>
        <Answer label="Gender" value={orDash(formatEnumLabel(answers.gender))} />
        <Answer label="Age" value={ageOf(answers)} />
        <Answer label="Profession" value={orDash(answers.profession)} />
        <Answer label="Lifestyle" value={orDash(answers.lifestyle)} />
      </Section>

      <Section title="Contact information" columns={3}>
        <Answer label="Phone" value={orDash(answers.phone)} />
        <Answer label="Email" value={orDash(answers.email)} />
        <Answer
          label="Home address"
          value={orDash(
            [answers.address, answers.city, answers.state, answers.postalCode]
              .filter((part) => part.trim())
              .join(", ")
          )}
        />
      </Section>

      <Section title="Health metrics" columns={4}>
        <Answer
          label="Height"
          value={measure(answers.height, answers.heightUnit)}
        />
        <Answer
          label="Weight"
          value={measure(answers.weight, answers.weightUnit)}
        />
        <Answer
          label="Target weight"
          value={measure(answers.targetWeight, answers.targetWeightUnit)}
        />
        <Answer label="Goal" value={asLine(answers.goals)} />
      </Section>

      <Section title="Diet & preferences" columns={2}>
        <Answer label="Food preference" value={orDash(answers.foodPreference)} />
        <Answer label="Favorite cuisines" value={asLine(answers.cuisines)} />
        <Answer label="Food allergies" value={asLine(answers.foodAllergies)} />
        <Answer label="Medical conditions" value={asLine(answers.conditions)} />
        {/* Only the list, never the yes-or-no beside it: a client who answered
            No has no medications, and "No" is not the name of a medicine. */}
        <Answer label="Medications" value={asLine(answers.medications)} />
        <Answer label="Dishes you love" value={asLine(answers.dishesLoved)} />
        <Answer label="Dishes you avoid" value={asLine(answers.dishesAvoided)} />
      </Section>

      <Section title="Daily habits" columns={3}>
        <Answer
          label="Travel frequency"
          value={orDash(habitLabel(answers.travel))}
        />
        <Answer label="Alcohol" value={orDash(habitLabel(answers.alcohol))} />
        <Answer label="Smoking" value={orDash(habitLabel(answers.smoking))} />
        <Answer
          label="Working hours"
          value={hours(
            answers.workStart,
            answers.workEnd,
            answers.noFixedWorkHours
          )}
        />
        <Answer
          label="Sleep timings"
          value={hours(
            answers.sleepStart,
            answers.sleepEnd,
            answers.noFixedSleep
          )}
        />
      </Section>
    </div>
  );

  if (isMobile) {
    return (
      // Titled by the client, the way the sheet titles everything it carries.
      // The pill cannot sit beside that title — the sheet takes a string, not
      // a slot — so it heads the answers instead, which is where the question
      // it is asking about actually starts.
      <BottomSheet
        open
        title={clientName}
        onClose={onClose}
        footer={decisions}
      >
        <div className="px-5">
          <div className="pb-3">
            <PendingPill />
          </div>
          {sections}
        </div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open
      title={clientName}
      onClose={onClose}
      hideTitle
      className="max-w-[960px] h-[620px]"
      scrollBody
      header={
        /* A slot of its own rather than `title` so the pill can stand beside
           the name, and so the pair stays put while the answers scroll under
           them. The dialog is still named by `title` to a screen reader — see
           `hideTitle`. */
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-[18px] 3xl:text-[20px] font-medium text-ink">
            {clientName}
          </h2>
          <PendingPill />
        </div>
      }
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {decisions}
        </div>
      }
    >
      {sections}
    </Modal>
  );
};

export default PreconsultationReviewModal;
